import type { ApiConnectionPlatform } from '../types/apiConnections.js';

// Token staleness — the heuristic that a platform's OAuth grant has been REVOKED
// or has expired, while every other field on the connection still reads healthy.
//
// WHY A HEURISTIC AND NOT A FLAG: the 2026-07-30 Google Ads episode (Liverpool
// Mazda / Mount Druitt / Crikey Customs went dead within hours of connecting, per
// Miki because Google revoked the OAuth tokens) showed Zernio's OWN health fields
// stayed clean throughout — `needsReconnection: false`, `adsStatus: 'connected'`,
// `adsSyncFailureCount: 0`, `analyticsLastSyncError: null`. The only tell in the
// payload was `tokenExpiresAt` sitting in the past. So this is the best signal
// available, NOT a definitive one — every surface built on it must say "may need
// reconnecting", never "dead". (Open question with Miki: is there a field that
// DEFINITIVELY indicates a revoked token? Switch to it if one appears.)
//
// ⚠️ THE NUANCE THAT MAKES THE THRESHOLD LOAD-BEARING: `tokenExpiresAt` a few
// minutes — or even a few hours — in the past is COMPLETELY NORMAL. That's the
// short-lived ACCESS token lapsing; it is refreshed on next use. A healthy
// connection refreshes roughly hourly, so only a LONG lapse means the refresh
// itself is failing (i.e. the refresh token is gone). Without the threshold this
// check would flag most of the roster most of the time. Observed evidence for the
// default: the revoked accounts read ~2 DAYS past expiry.

/**
 * How far past `tokenExpiresAt` a connection must be before we call it stale.
 * 24h ≫ the ~1h refresh cycle, so a normal access-token lapse never trips it.
 */
export const TOKEN_STALE_HOURS = 24;

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Hours elapsed since `expiresAt`, or null when there is nothing to measure —
 * absent/blank/unparseable input, or an expiry still in the FUTURE (the healthy
 * case). Never negative.
 *
 * Fails SAFE (null) on a shape we can't read: an unparseable timestamp must not
 * become a reconnect prompt for an account that is probably fine.
 */
export function tokenStaleHours(
  expiresAt: string | null | undefined,
  nowMs: number,
): number | null {
  if (typeof expiresAt !== 'string' || expiresAt.trim() === '') return null;
  const expiry = Date.parse(expiresAt);
  if (Number.isNaN(expiry)) return null;
  const elapsedMs = nowMs - expiry;
  if (elapsedMs <= 0) return null; // not yet expired
  return elapsedMs / MS_PER_HOUR;
}

/**
 * True when the token expired long enough ago that the refresh clearly isn't
 * happening — the "may need reconnecting" signal. Boundary is INCLUSIVE of the
 * threshold (exactly 24h stale counts).
 */
export function isTokenStale(
  expiresAt: string | null | undefined,
  nowMs: number,
  thresholdHours: number = TOKEN_STALE_HOURS,
): boolean {
  const hours = tokenStaleHours(expiresAt, nowMs);
  return hours !== null && hours >= thresholdHours;
}

/** The platforms in a per-platform expiry map whose token reads stale. */
export function stalePlatforms(
  tokenExpiry: Partial<Record<ApiConnectionPlatform, string | null>> | null | undefined,
  nowMs: number,
  thresholdHours: number = TOKEN_STALE_HOURS,
): ApiConnectionPlatform[] {
  if (!tokenExpiry) return [];
  return (Object.keys(tokenExpiry) as ApiConnectionPlatform[]).filter((p) =>
    isTokenStale(tokenExpiry[p], nowMs, thresholdHours),
  );
}

/** "31h" / "2d" / "3w" — how long a token has been stale, for a chip or cell. */
export function formatStaleFor(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours) || hours <= 0) return '—';
  if (hours < 48) return `${Math.floor(hours)}h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}
